import type { ArtMetadata, ArtTextStyle, Offline, Slot } from "../schedule-presets/art-metadata";
import type { MetadataDay, MetadataTarget } from "../metadata-history/metadata-targets";
import { layoutCanvasPoint, layoutLocalPoint } from "../schedule-art/layout-coordinates";
import { weekdayKeys } from "../schedule-time/schedule-dates";

export type DayLayoutMode = "offline" | "one_slot" | "two_slot";
type DayAnchors = Record<MetadataDay, number[]>;
type OfflineLayout = { mode: "offline"; shared: Offline[]; exceptions: Partial<Record<MetadataDay, Offline[]>>; anchors: DayAnchors };
export type StreamLayout = { mode: "one_slot" | "two_slot"; shared: Slot[]; exceptions: Partial<Record<MetadataDay, Slot[]>>; anchors: DayAnchors };
export type BuilderDayLayout = OfflineLayout | StreamLayout;
export type BuilderDayLayouts = Partial<Record<DayLayoutMode, BuilderDayLayout>>;
export type DayLayoutAction = "initialize" | "anchor" | "placement" | "rotation" | "exception" | "shared" | "element" | "copy";

export function builderDayLayoutMode(step: string): DayLayoutMode | undefined {
  if (step === "offline") return "offline";
  if (step === "oneSlot") return "one_slot";
  if (step === "twoSlot") return "two_slot";
  return undefined;
}

export function materializeDayLayout(metadata: ArtMetadata, layout: BuilderDayLayout, styles: Record<string, ArtTextStyle>) {
  const days = { ...metadata.days };
  weekdayKeys.forEach(day => {
    const original = metadata.days[day];
    const anchor = layout.anchors[day];
    const materialize = <Element extends Offline | Slot>(elements: Element[]) => elements.map(element => ({
      ...element,
      offset: layoutLocalPoint(layoutCanvasPoint(element.offset, anchor, original.rotation), original.origin, original.rotation),
    }));
    // Keep the exported origin stable; only this mode's offsets carry its anchor.
    if (layout.mode === "offline") days[day] = { ...original, offline: materialize(layout.exceptions[day] ?? layout.shared) };
    else days[day] = { ...original, [layout.mode]: materialize(layout.exceptions[day] ?? layout.shared) };
  });
  return { ...metadata, days, styles: { ...metadata.styles, ...styles } };
}

export function dayLayoutTargets(targets: MetadataTarget[], layout: BuilderDayLayout | undefined, previewDays: MetadataDay[] | null) {
  const visible = targets.filter(target => {
    if (previewDays && target.day && !previewDays.includes(target.day)) return false;
    if (!layout || !target.day || target.bind === "origin" || target.bind === "day.date") return true;
    return target.path.split(".")[2] === layout.mode;
  });
  return visible.map(target => {
    if (target.bind !== "origin" || !target.day || !layout) return target;
    return { ...target, point: layout.anchors[target.day] };
  });
}

export function moveDayLayout(metadata: ArtMetadata, layout: BuilderDayLayout, path: string, point: number[]) {
  const [, key, section, number] = path.split(".");
  const day = weekdayKeys.find(day => day === key);
  if (!day || point.length !== 2 || !point.every(Number.isFinite)) return null;
  const next = structuredClone(layout);
  if (section === "origin") {
    next.anchors[day] = point.map(Math.round);
    return { layout: next, day, action: "anchor" as const };
  }
  if (section !== layout.mode) return null;
  const elements = next.exceptions[day] ?? next.shared;
  const element = elements[Number(number)];
  if (!element) return null;
  const stable = metadata.days[day];
  const canvasPoint = layoutCanvasPoint(point, stable.origin, stable.rotation);
  element.offset = layoutLocalPoint(canvasPoint, layout.anchors[day], stable.rotation).map(Math.round);
  return { layout: next, day: next.exceptions[day] ? day : undefined, action: "placement" as const };
}

export function dayLayoutException(metadata: ArtMetadata, layout: BuilderDayLayout, day: MetadataDay, enabled: boolean) {
  const next = structuredClone(layout);
  const styles: Record<string, ArtTextStyle> = {};
  const formats: Record<string, string> = {};
  if (!enabled) delete next.exceptions[day];
  else {
    const elements = structuredClone(layout.exceptions[day] ?? layout.shared);
    elements.forEach((element, index) => {
      if ("format" in element && element.format) {
        const pattern = metadata.formats[element.format];
        if (pattern !== undefined) {
          const name = `builder_${layout.mode}_${day}_${index}_format`;
          formats[name] = pattern;
          element.format = name;
        }
      }
      if (!element.style) return;
      const source = metadata.styles[element.style];
      if (!source) return;
      const name = `builder_${layout.mode}_${day}_${index}`;
      styles[name] = structuredClone(source);
      element.style = name;
    });
    if (next.mode === "offline") next.exceptions[day] = elements as Offline[];
    else next.exceptions[day] = elements as Slot[];
  }
  return { layout: next, styles, formats };
}
